import type { ColumnDef } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import { DataTable } from '@/components/data-table/data-table';
import { InfiniteScrollSentinel } from '@/components/data-table/infinite-scroll-sentinel';
import { QueryErrorBanner } from '@/components/feedback/query-error';
import { isNotFoundError } from '@/lib/api/error';
import type { WsTrade } from '@/lib/websocket';
import { type TradeRow, wsTradeToRow } from '@/services/api/trades';
import { TRADE_TABLE_COLUMNS } from './trade-columns';
import './trading.css';

// Stable fallback — inline `?? []` would defeat DataTable's data-identity guard
const EMPTY_ROWS: TradeRow[] = [];

// Every row belongs to this user/their subaccounts by default — Account Holder column
// hidden (legacy show-account-holder={false}); TradingPanel overrides via the
// `columns` prop to show the full ops column set including accountHolderId.
const DEFAULT_COLUMNS: ColumnDef<TradeRow, unknown>[] = TRADE_TABLE_COLUMNS.filter((column) => !('accessorKey' in column && column.accessorKey === 'accountHolderId'));

const getTradeRowId = (row: TradeRow) => row.tradeId;

/** Minimal structural view of a useInfiniteQuery result — the exchange and instant hooks both fit. */
export interface TradesListQuery {
  data: TradeRow[] | undefined;
  error: unknown;
  isError: boolean;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => unknown;
  refetch: () => unknown;
}

interface TradesListProps {
  query: TradesListQuery;
  liveTrades: WsTrade[];
  flashTradeIds: ReadonlySet<string>;
  hasActiveFilters: boolean;
  emptyMessage: string;
  endMessage: string;
  errorTitle: string;
  /**
   * Column set. Defaults to the account-holder-filtered set (user-console usage);
   * TradingPanel passes the full TRADE_TABLE_COLUMNS to also show accountHolderId.
   */
  columns?: ColumnDef<TradeRow, unknown>[];
}

/**
 * Shared infinite trade list for the Trading page and both user-console Trades sub-tabs:
 * REST pages with live WS rows on top, flash styling (trading.css), inline error with
 * Retry (fix — legacy trading page latched a failed page forever until remount; legacy
 * user-console only console.error'd with no retry), and 404 → empty state, not the fatal
 * error state. TradingPanel additionally shows an account-holder-specific toast on top of
 * this 404-as-empty behaviour, kept local to TradingPanel because it depends on `filters`
 * state this component doesn't have.
 */
export const TradesList = ({ query, liveTrades, flashTradeIds, hasActiveFilters, emptyMessage, endMessage, errorTitle, columns = DEFAULT_COLUMNS }: TradesListProps) => {
  const { data, error, fetchNextPage, hasNextPage, isError, isFetchingNextPage, isLoading, refetch } = query;

  // Unknown pid/account holder → the per-user endpoint 404s: "no trades", not a fatal
  // error
  const isNotFound = isError && isNotFoundError(error);

  const rows = useMemo(() => {
    const pageRows = data ?? EMPTY_ROWS;
    // Live rows ignore the server-side filters — suppress them in filtered views instead
    // of re-implementing every predicate client-side (deliberate fix)
    if (hasActiveFilters || liveTrades.length === 0) return pageRows;
    // Dedupe by tradeId — drop a WS row once the same id appears in REST data. Chosen fix
    // over legacy, and guards a future `trade`-stream revival from
    // double-showing rows (deliberate fix); legacy could show duplicates after
    // a refetch.
    const seenTradeIds = new Set(pageRows.map((row) => row.tradeId));
    const liveRows: TradeRow[] = [];
    for (const trade of liveTrades) {
      if (seenTradeIds.has(trade.tradeId)) continue;
      seenTradeIds.add(trade.tradeId);
      liveRows.push(wsTradeToRow(trade));
    }
    // Live rows on top, matching legacy
    return [...liveRows, ...pageRows];
  }, [data, liveTrades, hasActiveFilters]);

  const tableRows = isNotFound ? EMPTY_ROWS : rows;

  // Instant tint on insert, ~2 s hold, 1 s fade on class removal — see trading.css
  const rowClassName = useCallback(
    (row: TradeRow) => {
      if (!flashTradeIds.has(row.tradeId)) return 'trade-row';
      return row.side === 'BUY' ? 'trade-row flash-buy' : 'trade-row flash-sell';
    },
    [flashTradeIds],
  );

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage && !isError) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, isError, fetchNextPage]);

  if (isError && !isNotFound) {
    // Legacy trading page latched this error state forever — one failed page killed the
    // whole page until remount (deliberate fix); legacy user-console only
    // console.error'd with no retry (deliberate fix). Same copy, but with Retry.
    return <QueryErrorBanner message="Please try again later." onRetry={() => void refetch()} title={errorTitle} />;
  }

  return (
    <>
      <DataTable columns={columns} data={tableRows} emptyMessage={emptyMessage} getRowId={getTradeRowId} isLoading={isLoading} rowClassName={rowClassName} />
      {/* Pages load on scroll only — no auto-fetch-all */}
      {hasNextPage && !isNotFound ? <InfiniteScrollSentinel hasMore={hasNextPage} isLoading={isFetchingNextPage} onLoadMore={handleLoadMore} /> : null}
      {/* Legacy end-of-data caption, only when rows exist and nothing is loading */}
      {!hasNextPage && !isLoading && !isFetchingNextPage && tableRows.length > 0 ? <p className="py-2 text-center text-sm text-muted-foreground">{endMessage}</p> : null}
    </>
  );
};
TradesList.displayName = 'TradesList';
